import React from 'react';
import { motion } from 'framer-motion';
import { targetName } from '../utils/portalEnvs';
import { target } from '../config/awsExports';

// This component is displayed when the user has no projects assigned to them.
// It provides a link to request access to projects and contact the IoT team for further assistance.
const pageVariants = {
  initial: {
    opacity: 0,
    y: -20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const iconVariants = {
  initial: {
    scale: 0,
    rotate: -180,
  },
  animate: {
    scale: 1,
    rotate: 0,
    transition: { 
      type: "spring",
      stiffness: 200,
      damping: 15,
      delay: 0.2
    },
  },
};

const buttonVariants = {
  hover: {
    scale: 1.02,
    y: -2,
    transition: { duration: 0.2 }
  },
  tap: {
    scale: 0.98
  }
};

const NoProjectsPage = () => (
  <motion.div
    initial="initial"
    animate="animate"
    variants={pageVariants}
    className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 p-4"
  >
    <div className="w-full max-w-2xl">
      <motion.div
        className="rounded-2xl bg-white p-8 shadow-2xl md:p-12"
      >
        {/* Icon */}
        <motion.div
          variants={iconVariants}
          className="mx-auto mb-8 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 shadow-lg"
        >
          <svg 
            className="h-12 w-12 text-white" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
            />
          </svg>
        </motion.div>

        {/* Title */}
        <h1 className="mb-4 text-center text-3xl font-bold text-gray-800 sm:text-4xl">
          No Projects Assigned
        </h1>
        
        {/* Subtitle */}
        <p className="mb-8 text-center text-lg text-gray-600">
          Your authentication was successful! 🎉
        </p>

        {/* Description */}
        <div className="mb-10 space-y-4 text-center text-gray-600">
          <p className="text-base leading-relaxed">
            You currently don&apos;t have any projects assigned to your account.
            To get started, please request access to the projects you need.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="space-y-4">
          {/* Primary CTA - ServiceNow */}
          <motion.a
            href="https://nestle.service-now.com/itsp?id=nesa_itsp_sc_cat_item&sys_id=70217c3b1b4ee15430fdfc49cd4bcbe2&sysparm_domain_restore=false&sysparm_stack=no"
            target="_blank"
            rel="noopener noreferrer"
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            className="group flex w-full items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-4 font-semibold text-white shadow-lg transition-all hover:shadow-xl"
          >
            <svg 
              className="h-5 w-5" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 4v16m8-8H4" 
              />
            </svg>
            <span>Request Project Access</span>
            <svg 
              className="h-5 w-5 transition-transform group-hover:translate-x-1" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M13 7l5 5m0 0l-5 5m5-5H6" 
              />
            </svg>
          </motion.a>

          {/* Secondary CTA - Contact Team */}
          <motion.a
            href={`mailto:iot-portal@internal.nestle.com?subject=%5BPortal-${targetName[target]}%5D%20Access%20request%20from%20portal%20user`}
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            className="group flex w-full items-center justify-center gap-3 rounded-xl border-2 border-gray-300 bg-white px-8 py-4 font-semibold text-gray-700 shadow-sm transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-700 hover:shadow-md"
          >
            <svg 
              className="h-5 w-5" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" 
              />
            </svg>
            <span>Contact IoT Team</span>
          </motion.a>
        </div>

        {/* Footer Info */}
        <div className="mt-10 rounded-lg bg-blue-50 p-4 text-center">
          <p className="text-sm text-gray-600">
            <span className="font-semibold text-blue-700">Need help?</span> Our team is here to assist you with any questions or access requests.
          </p>
        </div>
      </motion.div>
    </div>
  </motion.div>
);

export default NoProjectsPage;
